import React from 'react';
import { FiEye, FiX } from 'react-icons/fi';
import { useRolePreview, PREVIEWABLE_ROLES, PreviewableRole } from '../../contexts/RolePreviewContext';

/**
 * Sticky banner when superadmin is previewing another stakeholder role.
 * Always uses realRole for exit affordance (never gated by effectiveRole).
 */
const RolePreviewBanner: React.FC = () => {
  const { isPreviewing, previewRole, realRole, clearPreview, setPreviewRole, canUseRolePreview } =
    useRolePreview();

  if (!canUseRolePreview || !isPreviewing || !previewRole) return null;

  return (
    <div
      className="sticky top-16 z-30 w-full bg-amber-50 border-b border-amber-300 text-amber-950 px-4 py-2 flex flex-wrap items-center gap-3 text-sm shadow-sm"
      role="status"
    >
      <FiEye className="flex-shrink-0 text-amber-700" aria-hidden />
      <span className="font-medium">
        GUI preview as <code className="px-1 rounded bg-amber-100">{previewRole}</code>
      </span>
      <span className="text-amber-800/90">
        Nav and page buttons follow this role. Your login stays <code className="px-1 rounded bg-amber-100">{realRole}</code>
        {' '}(API is not impersonated).
      </span>
      <label className="ml-auto flex items-center gap-2">
        <span className="text-amber-800/80 whitespace-nowrap">Preview as</span>
        <select
          className="border border-amber-400 rounded px-2 py-1 bg-white text-ink-primary"
          value={previewRole}
          onChange={(e) => setPreviewRole(e.target.value as PreviewableRole)}
          aria-label="Change preview role"
        >
          {PREVIEWABLE_ROLES.filter((r) => r !== 'superadmin').map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={clearPreview}
        className="inline-flex items-center gap-1 px-3 py-1 rounded bg-amber-800 text-white hover:bg-amber-900 font-medium"
      >
        <FiX aria-hidden />
        Exit preview
      </button>
    </div>
  );
};

export default RolePreviewBanner;
